import { useEffect, useState } from 'react';

import { subscribeToUpdateAvailable, updateSW } from '../sw-register.js';
import { XIcon } from './icons';

/**
 * "A new version of Spotlight is available" — system-chrome banner
 * (card 110).
 *
 * Repurposes the slot we used to hand to the SDK's `.spot-banner` for
 * authored "welcome" content (which blocked the menu bar; the SDK
 * banner is now hidden on the dashboard — see
 * `dashboard/src/index.css`).
 *
 * Visibility chain:
 *
 *   1. `sw-register.js` registers the SW with `registerType:
 *      'autoUpdate'` (card 101).  In the common case the new SW
 *      activates + the next render reloads the tab silently — no
 *      banner needed.
 *
 *   2. For tabs held across a deploy (the operator left an editor
 *      mid-save overnight, the tab hasn't ticked since the SW
 *      activated), the `onNeedRefresh` callback fires instead.  We
 *      can't auto-reload those tabs without discarding work, so the
 *      sw-register module flips a flag + notifies subscribers.
 *
 *   3. This banner subscribes on mount and renders when the flag
 *      lands.  Reload calls `updateSW(true)` which forces the
 *      waiting SW to activate + reloads the tab; the operator picks
 *      the moment.
 *
 * Dismissable per session — once an operator clicks the X, the
 * banner stays hidden until they open a fresh tab.  We don't persist
 * across tabs/sessions because the next deploy will fire
 * `onNeedRefresh` again anyway, and we want the new version's banner
 * to surface even if a previous one was dismissed.
 *
 * Layout contract (matches the card 110 spec):
 *   - Sits ABOVE every other chrome surface; the HeaderBar and grid
 *     don't reflow when it appears (Shell.jsx puts it in a `auto`-
 *     sized row that's 0px tall when this component renders `null`).
 *   - Brand-600 accent border + brand-50 surface so the affordance
 *     reads as "informational, not destructive".
 */

const DISMISS_KEY = 'spotlight.updateBannerDismissed.v1';

function readDismissed() {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function markDismissed() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(DISMISS_KEY, '1');
  } catch { /* private mode / quota — dismissal is non-essential */ }
}

export function UpdateAvailableBanner() {
  const [available, setAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(() => readDismissed());

  useEffect(() => {
    // Late-mount safe: subscribe replays the current flag if the SW
    // already fired before this banner mounted (see
    // `subscribeToUpdateAvailable` for the replay semantics).
    return subscribeToUpdateAvailable(() => setAvailable(true));
  }, []);

  if (!available || dismissed) {
    // Render nothing so Shell's grid row collapses to 0 height — the
    // HeaderBar stays exactly where it was before the SW notified.
    return null;
  }

  function handleReload() {
    // updateSW(true) tells the waiting SW to activate + reloads the
    // page once it does.  The browser drops this tab's JS at that
    // point; no need to clear local state first.
    updateSW(true);
  }

  function handleDismiss() {
    markDismissed();
    setDismissed(true);
  }

  return (
    <div className="update-banner" role="status" aria-live="polite">
      <span className="update-banner__dot" aria-hidden="true" />
      <span className="update-banner__copy">
        A new version of Spotlight is available.
      </span>
      <button
        type="button"
        className="btn btn--primary update-banner__cta"
        onClick={handleReload}
      >
        Reload
      </button>
      <button
        type="button"
        className="update-banner__close"
        onClick={handleDismiss}
        aria-label="Dismiss until next session"
        title="Dismiss until next session"
      >
        <XIcon size={16} />
      </button>
      <style>{UPDATE_BANNER_CSS}</style>
    </div>
  );
}

const UPDATE_BANNER_CSS = `
  .update-banner {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 16px;
    background: var(--brand-50, #eef2ff);
    border-bottom: 1px solid var(--brand-100, #e0e7ff);
    /* Brand accent — the only "the dashboard wants you to act" hue.
       Border-left is intentional: a thin coloured stripe reads as a
       system notice without the heaviness of a full-bleed solid bar. */
    box-shadow: inset 3px 0 0 0 var(--brand-600, #4f46e5);
    color: var(--text-1, #0f172a);
    font-size: 13.5px;
    line-height: 1.4;
  }
  .update-banner__dot {
    width: 8px;
    height: 8px;
    border-radius: 9999px;
    background: var(--brand-600, #4f46e5);
    flex: 0 0 8px;
  }
  .update-banner__copy { flex: 1; min-width: 0; }
  .update-banner__cta { height: 28px; padding: 0 12px; font-size: 12.5px; }
  .update-banner__close {
    background: transparent;
    border: 0;
    cursor: pointer;
    color: var(--text-3, #64748b);
    padding: 4px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .update-banner__close:hover {
    background: var(--brand-100, #e0e7ff);
    color: var(--text-1, #0f172a);
  }
`;
